import * as React from "react";

import type { Omittable } from "../lib/props";

export type UseControllableStateOptions<T> = Omittable<{
  /** Controlled value - if provided, component is in controlled mode */
  prop?: T;
  /** Default value for uncontrolled mode */
  defaultProp: T;
  /** Callback fired when value changes */
  onChange?: (value: T) => void;
}>;

/**
 * Hook for controlled/uncontrolled state management.
 *
 * When `prop` is provided, the component operates in controlled mode
 * and uses the external value. Otherwise, it manages state internally.
 *
 * In controlled mode, setValue provides an optimistic preview: the new
 * value is returned immediately (via a pending ref) until the parent
 * prop catches up from the async round-trip (e.g. Apollo mutation).
 */
export function useControllableState<T>({
  prop,
  defaultProp,
  onChange,
}: UseControllableStateOptions<T>): [T, (value: T) => void] {
  const [uncontrolled, setUncontrolled] = React.useState(defaultProp);
  const isControlled = prop !== undefined;

  // In controlled mode, use internal state as an optimistic preview until
  // the parent prop catches up. We track `propAtSet` (the prop value at the
  // time setValue was called) so we only clear the pending flag when prop
  // actually changes from the parent, not on the immediate re-render where
  // prop is still the old value.
  const pendingRef = React.useRef<{
    value: T;
    propAtSet: T | undefined;
    set: boolean;
  }>({
    value: defaultProp,
    propAtSet: undefined,
    set: false,
  });
  const propRef = React.useRef(prop);
  propRef.current = prop;

  if (
    isControlled &&
    pendingRef.current.set &&
    prop !== pendingRef.current.propAtSet
  ) {
    // Prop changed since we set pending - parent responded, clear optimistic preview
    pendingRef.current.set = false;
  }

  const value = isControlled
    ? pendingRef.current.set
      ? pendingRef.current.value
      : prop
    : uncontrolled;

  const valueRef = React.useRef(value);
  valueRef.current = value;

  const setValue = React.useCallback(
    (nextValue: T) => {
      if (Object.is(nextValue, valueRef.current)) return;
      pendingRef.current = {
        value: nextValue,
        propAtSet: propRef.current,
        set: true,
      };
      setUncontrolled(nextValue);
      onChange?.(nextValue);
    },
    [onChange],
  );

  return [value, setValue];
}
